/**
 * S2 icon adapter — makes Spectrum 2 (`@react-spectrum/s2`) icons the PRIMARY
 * icon set while presenting a v3-compatible API to call sites.
 *
 * Why this exists:
 *  - S2 `IconProps` has NO `size` and NO `color` prop (sizing/color are meant to
 *    be driven by the S2 style macro, which is unavailable in our CRA build), and
 *    S2 icon component types are rejected by our JSX typings. Call sites, however,
 *    still pass v3 props like `size="S"` / `color="negative"`.
 *  - S2 icons don't consume v3 `SlotProvider` props, so placed inside a v3
 *    `Button`/`ActionButton`/`MenuItem` they'd miss v3's icon slot sizing and the
 *    icon→label gap.
 *
 * What `wrapS2` does:
 *  1. Consumes v3 slot props via `useSlotProps(props, 'icon')`. Inside a v3 button
 *     this forces `size` to the slot's value (`S`) and concatenates the hashed
 *     `spectrum-Icon` class onto our UNSAFE_className — so the S2 <svg> matches the
 *     `.spectrum-Icon + .spectrum-Button-label` adjacency selector and gets the
 *     same size + gap as the v3 workflow icon it replaces.
 *  2. Translates `size` / `color` into unlayered CSS classes (see `s2.css`) that
 *     reliably beat S2's layered defaults, using existing design tokens.
 *  3. Forwards `UNSAFE_className` / `UNSAFE_style` / aria / id to the S2 icon.
 *
 * Call sites import named wrapped icons from here instead of `@spectrum-icons/*`
 * and instead of the old per-file `const asS2Icon = (icon) => icon as React.FC`.
 */
import React, { useContext } from 'react';
import { useSlotProps } from '@react-spectrum/utils';
import { IconContext as S2IconContext } from '@react-spectrum/s2';
import './s2.css';

// --- Section A: same-name workflow -> s2/icons drop-ins ---
import S2_Add from '@react-spectrum/s2/icons/Add';
import S2_Archive from '@react-spectrum/s2/icons/Archive';
import S2_Building from '@react-spectrum/s2/icons/Building';
import S2_Calendar from '@react-spectrum/s2/icons/Calendar';
import S2_Cancel from '@react-spectrum/s2/icons/Cancel';
import S2_Chat from '@react-spectrum/s2/icons/Chat';
import S2_Checkmark from '@react-spectrum/s2/icons/Checkmark';
import S2_CheckmarkCircle from '@react-spectrum/s2/icons/CheckmarkCircle';
import S2_ChevronDown from '@react-spectrum/s2/icons/ChevronDown';
import S2_ChevronLeft from '@react-spectrum/s2/icons/ChevronLeft';
import S2_ChevronRight from '@react-spectrum/s2/icons/ChevronRight';
import S2_ChevronUp from '@react-spectrum/s2/icons/ChevronUp';
import S2_Clock from '@react-spectrum/s2/icons/Clock';
import S2_Close from '@react-spectrum/s2/icons/Close';
import S2_Copy from '@react-spectrum/s2/icons/Copy';
import S2_DataUpload from '@react-spectrum/s2/icons/DataUpload';
import S2_Delete from '@react-spectrum/s2/icons/Delete';
import S2_DeviceDesktop from '@react-spectrum/s2/icons/DeviceDesktop';
import S2_DevicePhone from '@react-spectrum/s2/icons/DevicePhone';
import S2_DeviceTablet from '@react-spectrum/s2/icons/DeviceTablet';
import S2_Download from '@react-spectrum/s2/icons/Download';
import S2_Edit from '@react-spectrum/s2/icons/Edit';
import S2_Feedback from '@react-spectrum/s2/icons/Feedback';
import S2_Filter from '@react-spectrum/s2/icons/Filter';
import S2_Folder from '@react-spectrum/s2/icons/Folder';
import S2_FolderSearch from '@react-spectrum/s2/icons/FolderSearch';
import S2_FullScreen from '@react-spectrum/s2/icons/FullScreen';
import S2_History from '@react-spectrum/s2/icons/History';
import S2_Key from '@react-spectrum/s2/icons/Key';
import S2_Layers from '@react-spectrum/s2/icons/Layers';
import S2_Link from '@react-spectrum/s2/icons/Link';
import S2_MagicWand from '@react-spectrum/s2/icons/MagicWand';
import S2_More from '@react-spectrum/s2/icons/More';
import S2_OpenIn from '@react-spectrum/s2/icons/OpenIn';
import S2_Play from '@react-spectrum/s2/icons/Play';
import S2_Preview from '@react-spectrum/s2/icons/Preview';
import S2_Redo from '@react-spectrum/s2/icons/Redo';
import S2_Revert from '@react-spectrum/s2/icons/Revert';
import S2_SaveFloppy from '@react-spectrum/s2/icons/SaveFloppy';
import S2_Search from '@react-spectrum/s2/icons/Search';
import S2_Target from '@react-spectrum/s2/icons/Target';
import S2_ThumbDown from '@react-spectrum/s2/icons/ThumbDown';
import S2_ThumbUp from '@react-spectrum/s2/icons/ThumbUp';
import S2_Undo from '@react-spectrum/s2/icons/Undo';
import S2_UploadToCloud from '@react-spectrum/s2/icons/UploadToCloud';
import S2_UserAdd from '@react-spectrum/s2/icons/UserAdd';
import S2_UserGroup from '@react-spectrum/s2/icons/UserGroup';
import S2_ViewGrid from '@react-spectrum/s2/icons/ViewGrid';
import S2_ViewList from '@react-spectrum/s2/icons/ViewList';
import S2_Visibility from '@react-spectrum/s2/icons/Visibility';
import S2_VisibilityOff from '@react-spectrum/s2/icons/VisibilityOff';

// --- Section B: renames ---
import S2_AlertTriangle from '@react-spectrum/s2/icons/AlertTriangle';
import S2_File from '@react-spectrum/s2/icons/File';
import S2_FileText from '@react-spectrum/s2/icons/FileText';
import S2_Settings from '@react-spectrum/s2/icons/Settings';
import S2_GlobeGrid from '@react-spectrum/s2/icons/GlobeGrid';
import S2_ChartBarVert from '@react-spectrum/s2/icons/ChartBarVert';
import S2_HelpCircle from '@react-spectrum/s2/icons/HelpCircle';
import S2_InfoCircle from '@react-spectrum/s2/icons/InfoCircle';
import S2_Lock from '@react-spectrum/s2/icons/Lock';
import S2_Leave from '@react-spectrum/s2/icons/Leave';
import S2_Publish from '@react-spectrum/s2/icons/Publish';
import S2_MenuHamburger from '@react-spectrum/s2/icons/MenuHamburger';

// --- Section E: icons used by existing S2 call sites / custom-icon + inline-SVG replacements ---
import S2_Asset from '@react-spectrum/s2/icons/Asset';
import S2_ChartTrend from '@react-spectrum/s2/icons/ChartTrend';
import S2_Home from '@react-spectrum/s2/icons/Home';
import S2_Project from '@react-spectrum/s2/icons/Project';
import S2_UserSettings from '@react-spectrum/s2/icons/UserSettings';
import S2_Layout from '@react-spectrum/s2/icons/Layout';
import S2_AudioWave from '@react-spectrum/s2/icons/AudioWave';
import S2_Send from '@react-spectrum/s2/icons/Send';
import S2_Plugin from '@react-spectrum/s2/icons/Plugin';
import S2_Code from '@react-spectrum/s2/icons/Code';
import S2_Upload from '@react-spectrum/s2/icons/Upload';
import S2_New from '@react-spectrum/s2/icons/New';
import S2_FileAdd from '@react-spectrum/s2/icons/FileAdd';
import S2_DataSettings from '@react-spectrum/s2/icons/DataSettings';
import S2_BadgeVerified from '@react-spectrum/s2/icons/BadgeVerified';
import S2_SocialNetwork from '@react-spectrum/s2/icons/SocialNetwork';
import S2_Data from '@react-spectrum/s2/icons/Data';
import S2_WebPage from '@react-spectrum/s2/icons/WebPage';

// --- Section C: linear illustrations ---
import S2_UploadIllu from '@react-spectrum/s2/illustrations/linear/Upload';
import S2_DropToUploadIllu from '@react-spectrum/s2/illustrations/linear/DropToUpload';
import S2_AIChatIllu from '@react-spectrum/s2/illustrations/linear/AIChat';
import S2_GraphBarChartIllu from '@react-spectrum/s2/illustrations/linear/GraphBarChart';
import S2_ClockIllu from '@react-spectrum/s2/illustrations/linear/Clock';
import S2_NoSearchResultsIllu from '@react-spectrum/s2/illustrations/linear/NoSearchResults';
import S2_BrowserErrorIllu from '@react-spectrum/s2/illustrations/linear/BrowserError';
import S2_LockCloseIllu from '@react-spectrum/s2/illustrations/linear/LockClose';
import S2_FolderCloseIllu from '@react-spectrum/s2/illustrations/linear/FolderClose';

/** v3 t-shirt sizes supported at call sites. */
export type IconSize = 'XXS' | 'XS' | 'S' | 'M' | 'L' | 'XL' | 'XXL';
/** v3 semantic icon colors supported at call sites. */
export type IconColor = 'negative' | 'positive' | 'informative' | 'notice';

export interface S2IconProps {
  /** v3 t-shirt size; mapped to the same px as v3 workflow icons. Defaults to `M`,
   *  except inside S2 components, where S2's native icon sizing applies. */
  size?: IconSize;
  /** v3 semantic color; mapped to the matching v3 semantic icon token. */
  color?: IconColor;
  UNSAFE_className?: string;
  UNSAFE_style?: React.CSSProperties;
  'aria-label'?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
  /** v3 slot name; defaults to `icon` so in-button placement is picked up. */
  slot?: string | null;
  id?: string;
}

const SIZE_CLASS: Record<IconSize, string> = {
  XXS: 'fb-s2-icon--xxs',
  XS: 'fb-s2-icon--xs',
  S: 'fb-s2-icon--s',
  M: 'fb-s2-icon--m',
  L: 'fb-s2-icon--l',
  XL: 'fb-s2-icon--xl',
  XXL: 'fb-s2-icon--xxl',
};

const COLOR_CLASS: Record<IconColor, string> = {
  negative: 'fb-s2-icon--negative',
  positive: 'fb-s2-icon--positive',
  informative: 'fb-s2-icon--informative',
  notice: 'fb-s2-icon--notice',
};

// S2 icon/illustration component types are rejected by our JSX typings, so we
// treat the raw default export as a loose component. This single cast replaces
// the per-file `asS2Icon` duplicates that previously lived in 7 components.
type RawS2Component = React.FC<Record<string, unknown>>;

type S2WrappedIcon = (props: S2IconProps) => React.ReactElement;

function wrapS2(raw: unknown, displayName: string): S2WrappedIcon {
  const S2Icon = raw as RawS2Component;
  const Wrapped = (props: S2IconProps): React.ReactElement => {
    // Participate in v3 slots (Button/ActionButton/MenuItem/...). Inside a v3
    // button this returns size forced to the slot value and the hashed
    // `spectrum-Icon` class concatenated onto UNSAFE_className.
    const merged = useSlotProps<S2IconProps>(props, 'icon');
    // S2 components (Button, ActionButton, MenuItem, ...) provide a non-empty
    // IconContext that sizes the glyph natively (e.g. 20px in an M button). Our
    // unlayered default `M` (24px) would override it, so only apply a size class
    // there when the caller asked for one explicitly.
    const s2IconCtx = useContext(S2IconContext);
    const sizedByS2 = merged.size === undefined && s2IconCtx != null && Object.keys(s2IconCtx).length > 0;
    const {
      size = 'M',
      color,
      UNSAFE_className,
      UNSAFE_style,
      // `slot` is intentionally dropped: forwarding it would make the S2 icon
      // try to read S2's own IconContext, which isn't present in our v3 tree.
      slot: _slot,
      ...rest
    } = merged;

    const className = [
      'fb-s2-icon',
      sizedByS2 ? 'fb-s2-icon--s2-slot' : (SIZE_CLASS[size] ?? SIZE_CLASS.M),
      color ? COLOR_CLASS[color] : null,
      UNSAFE_className,
    ]
      .filter(Boolean)
      .join(' ');

    return <S2Icon {...rest} UNSAFE_className={className} UNSAFE_style={UNSAFE_style} />;
  };
  Wrapped.displayName = `S2(${displayName})`;
  return Wrapped;
}

/** v3 illustration sizes natively supported by S2 linear illustrations. */
export interface S2IllustrationProps {
  /** S2 illustration size: S (48px), M (96px, default), L (160px). */
  size?: 'S' | 'M' | 'L';
  UNSAFE_className?: string;
  UNSAFE_style?: React.CSSProperties;
  'aria-label'?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
}

type S2WrappedIllustration = (props: S2IllustrationProps) => React.ReactElement;

function wrapS2Illustration(raw: unknown, displayName: string): S2WrappedIllustration {
  const S2Illustration = raw as RawS2Component;
  const Wrapped = (props: S2IllustrationProps): React.ReactElement => {
    const { UNSAFE_className, ...rest } = props;
    const className = ['fb-s2-illustration', UNSAFE_className].filter(Boolean).join(' ');
    return <S2Illustration {...rest} UNSAFE_className={className} />;
  };
  Wrapped.displayName = `S2Illustration(${displayName})`;
  return Wrapped;
}

// --- Section A exports ---
export const Add = wrapS2(S2_Add, 'Add');
export const Archive = wrapS2(S2_Archive, 'Archive');
export const Building = wrapS2(S2_Building, 'Building');
export const Calendar = wrapS2(S2_Calendar, 'Calendar');
export const Cancel = wrapS2(S2_Cancel, 'Cancel');
export const Chat = wrapS2(S2_Chat, 'Chat');
export const Checkmark = wrapS2(S2_Checkmark, 'Checkmark');
export const CheckmarkCircle = wrapS2(S2_CheckmarkCircle, 'CheckmarkCircle');
export const ChevronDown = wrapS2(S2_ChevronDown, 'ChevronDown');
export const ChevronLeft = wrapS2(S2_ChevronLeft, 'ChevronLeft');
export const ChevronRight = wrapS2(S2_ChevronRight, 'ChevronRight');
export const ChevronUp = wrapS2(S2_ChevronUp, 'ChevronUp');
export const Clock = wrapS2(S2_Clock, 'Clock');
export const Close = wrapS2(S2_Close, 'Close');
export const Copy = wrapS2(S2_Copy, 'Copy');
export const DataUpload = wrapS2(S2_DataUpload, 'DataUpload');
export const Delete = wrapS2(S2_Delete, 'Delete');
export const DeviceDesktop = wrapS2(S2_DeviceDesktop, 'DeviceDesktop');
export const DevicePhone = wrapS2(S2_DevicePhone, 'DevicePhone');
export const DeviceTablet = wrapS2(S2_DeviceTablet, 'DeviceTablet');
export const Download = wrapS2(S2_Download, 'Download');
export const Edit = wrapS2(S2_Edit, 'Edit');
export const Feedback = wrapS2(S2_Feedback, 'Feedback');
export const Filter = wrapS2(S2_Filter, 'Filter');
export const Folder = wrapS2(S2_Folder, 'Folder');
export const FolderSearch = wrapS2(S2_FolderSearch, 'FolderSearch');
export const FullScreen = wrapS2(S2_FullScreen, 'FullScreen');
export const History = wrapS2(S2_History, 'History');
export const Key = wrapS2(S2_Key, 'Key');
export const Layers = wrapS2(S2_Layers, 'Layers');
export const Link = wrapS2(S2_Link, 'Link');
export const MagicWand = wrapS2(S2_MagicWand, 'MagicWand');
export const More = wrapS2(S2_More, 'More');
export const OpenIn = wrapS2(S2_OpenIn, 'OpenIn');
export const Play = wrapS2(S2_Play, 'Play');
export const Preview = wrapS2(S2_Preview, 'Preview');
export const Redo = wrapS2(S2_Redo, 'Redo');
export const Revert = wrapS2(S2_Revert, 'Revert');
export const SaveFloppy = wrapS2(S2_SaveFloppy, 'SaveFloppy');
export const Search = wrapS2(S2_Search, 'Search');
export const Target = wrapS2(S2_Target, 'Target');
export const ThumbDown = wrapS2(S2_ThumbDown, 'ThumbDown');
export const ThumbUp = wrapS2(S2_ThumbUp, 'ThumbUp');
export const Undo = wrapS2(S2_Undo, 'Undo');
export const UploadToCloud = wrapS2(S2_UploadToCloud, 'UploadToCloud');
export const UserAdd = wrapS2(S2_UserAdd, 'UserAdd');
export const UserGroup = wrapS2(S2_UserGroup, 'UserGroup');
export const ViewGrid = wrapS2(S2_ViewGrid, 'ViewGrid');
export const ViewList = wrapS2(S2_ViewList, 'ViewList');
export const Visibility = wrapS2(S2_Visibility, 'Visibility');
export const VisibilityOff = wrapS2(S2_VisibilityOff, 'VisibilityOff');

// --- Section B exports (renames; named by the S2 glyph) ---
export const AlertTriangle = wrapS2(S2_AlertTriangle, 'AlertTriangle');
export const File = wrapS2(S2_File, 'File');
export const FileText = wrapS2(S2_FileText, 'FileText');
export const Settings = wrapS2(S2_Settings, 'Settings');
export const GlobeGrid = wrapS2(S2_GlobeGrid, 'GlobeGrid');
export const ChartBarVert = wrapS2(S2_ChartBarVert, 'ChartBarVert');
export const HelpCircle = wrapS2(S2_HelpCircle, 'HelpCircle');
export const InfoCircle = wrapS2(S2_InfoCircle, 'InfoCircle');
export const Lock = wrapS2(S2_Lock, 'Lock');
export const Leave = wrapS2(S2_Leave, 'Leave');
export const Publish = wrapS2(S2_Publish, 'Publish');
export const MenuHamburger = wrapS2(S2_MenuHamburger, 'MenuHamburger');

// --- Section E exports ---
export const Asset = wrapS2(S2_Asset, 'Asset');
export const ChartTrend = wrapS2(S2_ChartTrend, 'ChartTrend');
export const Home = wrapS2(S2_Home, 'Home');
export const Project = wrapS2(S2_Project, 'Project');
export const UserSettings = wrapS2(S2_UserSettings, 'UserSettings');
export const Layout = wrapS2(S2_Layout, 'Layout');
export const AudioWave = wrapS2(S2_AudioWave, 'AudioWave');
export const Send = wrapS2(S2_Send, 'Send');
export const Plugin = wrapS2(S2_Plugin, 'Plugin');
export const Code = wrapS2(S2_Code, 'Code');
export const Upload = wrapS2(S2_Upload, 'Upload');
export const New = wrapS2(S2_New, 'New');
export const FileAdd = wrapS2(S2_FileAdd, 'FileAdd');
// Semantic replacements for retired v3 main-app glyphs (user-approved 2026-10-06):
// DataSettings<-ColumnSettings, BadgeVerified<-IdentityService,
// SocialNetwork<-Branch3/Merge, Data<-DataCorrelated, WebPage<-FileHTML.
export const DataSettings = wrapS2(S2_DataSettings, 'DataSettings');
export const BadgeVerified = wrapS2(S2_BadgeVerified, 'BadgeVerified');
export const SocialNetwork = wrapS2(S2_SocialNetwork, 'SocialNetwork');
export const Data = wrapS2(S2_Data, 'Data');
export const WebPage = wrapS2(S2_WebPage, 'WebPage');

// --- Section C exports (illustrations) ---
export const UploadIllustration = wrapS2Illustration(S2_UploadIllu, 'Upload');
export const DropToUploadIllustration = wrapS2Illustration(S2_DropToUploadIllu, 'DropToUpload');
export const AIChatIllustration = wrapS2Illustration(S2_AIChatIllu, 'AIChat');
export const GraphBarChartIllustration = wrapS2Illustration(S2_GraphBarChartIllu, 'GraphBarChart');
export const ClockIllustration = wrapS2Illustration(S2_ClockIllu, 'Clock');
export const NoSearchResultsIllustration = wrapS2Illustration(S2_NoSearchResultsIllu, 'NoSearchResults');
export const BrowserErrorIllustration = wrapS2Illustration(S2_BrowserErrorIllu, 'BrowserError');
export const LockCloseIllustration = wrapS2Illustration(S2_LockCloseIllu, 'LockClose');
export const FolderCloseIllustration = wrapS2Illustration(S2_FolderCloseIllu, 'FolderClose');
